import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import "@/App.css";
import { IS_NATIVE } from "./lib/api";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import AuthPage from "./pages/Auth";
import GuestShell from "./components/guest/GuestShell";
import Discover from "./pages/guest/Discover";
import NightMap from "./pages/guest/NightMap";
import Pass from "./pages/guest/Pass";
import Rewards from "./pages/guest/Rewards";
import Profile from "./pages/guest/Profile";
import VenueProfile from "./pages/VenueProfile";
import PromoterProfile from "./pages/PromoterProfile";
import StaffScanner from "./pages/StaffScanner";
import VenueDirectory from "./pages/VenueDirectory";
import PromoterDirectory from "./pages/PromoterDirectory";
import PromoterDashboard from "./pages/promoter/PromoterDashboard";
import VenueDashboard from "./pages/venue/VenueDashboard";
import AdminDashboard from "./pages/admin/AdminDashboard";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentResult";
import { PrivacyPolicy, TermsOfService, DataPolicy } from "./pages/Legal";
import About from "./pages/About";

function Loading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505]">
      <img src="/logo.png" alt="NITEPASS" className="h-16 w-auto animate-pulse" />
    </div>
  );
}

function RequireAuth({ children, roles }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/auth" state={{ from: location.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  useEffect(() => {
    if (!IS_NATIVE) return;
    import("@capacitor/status-bar").then(({ StatusBar, Style }) => {
      StatusBar.setStyle({ style: Style.Dark }).catch(() => {});
      StatusBar.setBackgroundColor({ color: "#050505" }).catch(() => {});
    });
    import("@capacitor/splash-screen").then(({ SplashScreen }) =>
      SplashScreen.hide().catch(() => {}));
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/data-policy" element={<DataPolicy />} />
          <Route path="/about" element={<About />} />
          <Route path="/auth" element={<AuthPage />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route
            path="/venue/:id"
            element={<RequireAuth><VenueProfile /></RequireAuth>}
          />
          <Route
            path="/promoter"
            element={<RequireAuth roles={["promoter", "admin"]}><PromoterDashboard /></RequireAuth>}
          />
          <Route
            path="/venue-dash"
            element={<RequireAuth roles={["venue", "admin"]}><VenueDashboard /></RequireAuth>}
          />
          <Route
            path="/admin"
            element={<RequireAuth roles={["admin"]}><AdminDashboard /></RequireAuth>}
          />
          <Route element={<RequireAuth><GuestShell /></RequireAuth>}>
            <Route path="/app" element={<Discover />} />
            <Route path="/app/map" element={<NightMap />} />
            <Route path="/app/pass" element={<Pass />} />
            <Route path="/app/rewards" element={<Rewards />} />
            <Route path="/app/profile" element={<Profile />} />
          </Route>
          <Route
            path="/staff-scanner"
            element={<RequireAuth><StaffScanner /></RequireAuth>}
          />
          <Route path="/venues" element={<VenueDirectory />} />
          <Route path="/promoters" element={<PromoterDirectory />} />
          <Route path="/:handle" element={<PromoterProfile />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster theme="dark" position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
